<u2b-dialog class="max-h-[92vh] bg-p-content-bg rounded-[inherit]" contentClass="bg-p-surface">

    <div u2bDialogHeader class="contents">
        <h1 class="text-xl font-semibold m-0 flex items-center gap-2 flex-wrap text-p-primary-contrast">
            <span class="whitespace-nowrap">
                @if (facade.isEditMode()) {
                    @if (facade.bookingType() === 'reservation') {
                        {{ 'bookingV2.editTitleReservationWithNumber' | translate: { number: facade.bookingNumber() } }}
                    } @else {
                        {{ 'bookingV2.editTitleBookingWithNumber' | translate: { number: facade.bookingNumber() } }}
                    }
                } @else if (facade.bookingType() === 'reservation') {
                    {{ 'bookingV2.titleReservation' | translate }}
                } @else {
                    {{ 'bookingV2.titleBooking' | translate }}
                }
            </span>
            @if (facade.isDialogDirty()) {
                <span class="inline-flex items-center rounded-full bg-p-primary-contrast/15 px-2 py-0.5 text-xs font-medium text-p-primary-contrast whitespace-nowrap">
                    {{ 'bookingV2.unsavedDraftBadge' | translate }}
                </span>
            }
        </h1>

        <!-- The booking type is fixed once a booking exists, so the toggle only
             shows when creating. -->
        @if (!facade.isEditMode()) {
            <u2b-segmented-toggle
                [options]="bookingTypeOptions"
                [value]="facade.bookingType()"
                variant="on-primary"
                [ariaLabel]="('bookingV2.bookingType' | translate)"
                (valueChange)="facade.setBookingType($event)" />
        }

        @if (!loading()) {
            <div class="flex items-center gap-2 flex-shrink-0">
                <p-toggleswitch
                    inputId="advanced-mode-header-toggle"
                    [ngModel]="facade.advancedMode()"
                    (onChange)="facade.toggleAdvancedMode()"
                    [pTooltip]="('bookingV2.advancedBookingTooltip' | translate)"
                    tooltipPosition="bottom" />
                <label
                    for="advanced-mode-header-toggle"
                    class="text-sm font-medium text-p-primary-contrast cursor-pointer select-none m-0 whitespace-nowrap">
                    @if (facade.bookingType() === 'reservation') {
                        {{ 'bookingV2.advancedReservation' | translate }}
                    } @else {
                        {{ 'bookingV2.advancedBooking' | translate }}
                    }
                </label>
            </div>
        }

        <div class="flex items-center gap-1 flex-shrink-0 ml-auto">
            <!-- The onboarding tour is a create-flow walkthrough; hidden on edit. -->
            @if (!facade.isEditMode()) {
                <u2b-icon-button
                    variant="on-primary"
                    icon="help_outline"
                    [tooltip]="('bookingV2.headerHelp' | translate)"
                    [ariaLabel]="('bookingV2.headerHelp' | translate)"
                    (clicked)="facade.openTour()" />
            }
            <u2b-icon-button
                variant="on-primary"
                icon="close"
                [ariaLabel]="('common.close' | translate)"
                (clicked)="close()" />
        </div>
    </div>

    <!-- Each named slot stays in its own @if (single root node) so projection works. -->
    @if (!loading()) {
        <div u2bDialogSubheader class="flex flex-col">
            <!-- Reservation mode banner: full-width bar between header and stepper. -->
            @if (facade.bookingType() === 'reservation') {
                <div role="note"
                     class="flex items-center gap-2 px-6 py-3 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm leading-snug">
                    <span class="material-icons text-amber-600 text-lg leading-none flex-shrink-0" aria-hidden="true">assignment</span>
                    <span>
                        @if (facade.depositRequired()) {
                            {{ 'bookingV2.reservation.banner.withDeposit' | translate }}
                        } @else {
                            {{ 'bookingV2.reservation.banner.default' | translate }}
                        }
                    </span>
                </div>
            }
            <u2b-wizard-stepper
                [steps]="wizardSteps()"
                [currentId]="facade.currentStep()"
                [visitedIds]="null"
                [invalidIds]="invalidStepIds()"
                [attr.aria-label]="('bookingV2.stepIndicator.aria' | translate)"
                (stepSelected)="onStepSelected($event)" />
        </div>
    }

    @if (loading()) {
        <div class="flex flex-col items-center justify-center gap-4 py-20 text-p-text-muted">
            <p-progressSpinner strokeWidth="4" [style]="{ width: '3rem', height: '3rem' }" />
            <span class="text-sm">{{ 'bookingV2.loadingBooking' | translate }}</span>
        </div>
    } @else {
    @switch (facade.currentStep()) {
        @case ('date') {
            <booking-v2-date-range-step></booking-v2-date-range-step>
        }
        @case ('berth') {
            <booking-v2-berth-step></booking-v2-berth-step>
        }
        @case ('person') {
            <booking-v2-person-step></booking-v2-person-step>
        }
        @case ('advanced') {
            <booking-v2-advanced-step></booking-v2-advanced-step>
        }
        @case ('rechnungsversand') {
            <booking-v2-rechnungsversand-step></booking-v2-rechnungsversand-step>
        }
        @case ('summary') {
            <booking-v2-summary-step></booking-v2-summary-step>
        }
    }
    }

    @if (!loading()) {
    <div u2bDialogFooter class="flex flex-col gap-3 w-full min-w-0">
        @if (commitService.missingRequirements().length) {
            <div class="flex flex-col gap-2 px-3 py-3 rounded-lg bg-amber-50 border border-amber-200 w-full">
                <div class="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <span class="material-icons text-base" aria-hidden="true">warning</span>
                    {{ 'bookingV2.validation.heading' | translate }}
                </div>
                <ul class="list-none p-0 m-0 flex flex-col gap-1">
                    @for (req of commitService.missingRequirements(); track req.step) {
                        <li class="flex items-center justify-between gap-3">
                            <span class="text-sm text-amber-900">{{ req.labelKey | translate }}</span>
                            <p-button type="button" severity="warn" size="small" [text]="true" (onClick)="goToRequirementStep(req.step)">
                                <span pButtonLabel>{{ 'bookingV2.validation.goToStep' | translate }}</span>
                                <span pButtonIcon class="material-icons" style="font-size: 1rem">arrow_forward</span>
                            </p-button>
                        </li>
                    }
                </ul>
            </div>
        }
        <div class="flex items-center justify-between gap-2 w-full flex-wrap">
            <p-button
                [label]="'common.cancel' | translate"
                severity="secondary"
                [text]="true"
                (onClick)="close()">
            </p-button>
            <div class="flex items-center gap-2">
                <p-button
                    severity="secondary"
                    [outlined]="true"
                    [disabled]="facade.isFirstStep()"
                    (onClick)="prev()">
                    <span pButtonIcon class="material-icons" style="font-size: 1.125rem">arrow_back</span>
                    <span pButtonLabel>{{ 'common.back' | translate }}</span>
                </p-button>
                <p-button
                    [label]="saveButtonLabelKey() | translate"
                    [severity]="facade.isLastStep() ? undefined : 'secondary'"
                    [outlined]="!facade.isLastStep()"
                    [disabled]="!commitService.canCommit() || commitService.saving()"
                    [loading]="commitService.saving()"
                    [pTooltip]="saveButtonTooltipKey() | translate"
                    tooltipPosition="top"
                    (onClick)="save()">
                    @if (saveFeedbackActive()) {
                        <span pButtonIcon class="material-icons" style="font-size: 1.125rem">check</span>
                    }
                </p-button>
                @if (!facade.isLastStep() && facade.currentStep() !== 'advanced') {
                    <!-- Advanced step has its own in-card pager (6585); the footer next
                         would skip the remaining tabs, so it is suppressed there. -->
                    <p-button
                        [disabled]="!facade.canProceed()"
                        (onClick)="next()">
                        <span pButtonLabel>{{ 'common.next' | translate }}</span>
                        <span pButtonIcon class="material-icons" style="font-size: 1.125rem">arrow_forward</span>
                    </p-button>
                }
            </div>
        </div>
    </div>

    }

</u2b-dialog>

<!-- Post-save contract generator: opened after a create-flow save when armed via
     the summary toggle. Its own p-dialog (appendTo body) stacks above the wizard;
     closing it completes the save (Beleg + close). -->
@if (contractDialogOpen() && contractPersonOption(); as contractPerson) {
    <booking-v2-contract-dialog
        [visible]="true"
        (visibleChange)="onContractDialogVisibleChange($event)"
        [bookingId]="contractBookingId()"
        [person]="contractPerson"
        [boat]="contractBoatOption()"
        [berths]="contractBerthOptions()"
        [fromDate]="contractFrom()"
        [toDate]="contractTo()" />
}
